import React, { useEffect, useMemo, useState, createContext, useContext } from "react";
import { useLanguage } from "../i18n";

const STORAGE_KEY = "cookieConsent.v2";

const defaultChoices = {
  necessary: true,     // cannot be turned off
  functional: false,   // e.g., reCAPTCHA, map/video embeds
  analytics: false,    // e.g., GA (with IP anonymization)
  marketing: false,    // e.g., Meta/Ads
};

const Ctx = createContext(null);
export const useCookieConsent = () => useContext(Ctx);

export default function CookieConsentProvider({ children }) {
  const [choices, setChoices] = useState(defaultChoices);
  const [decided, setDecided] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Hydrate
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.choices) {
          setChoices({ ...defaultChoices, ...parsed.choices, necessary: true });
          setDecided(true);
          return;
        }
      }
    } catch {}
    // Not decided yet → show banner and block via overlay
    setShowBanner(true);
  }, []);

  const persist = (next) => {
    const payload = { choices: { ...next, necessary: true }, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const acceptAll = () => {
    const next = { necessary: true, functional: true, analytics: true, marketing: true };
    setChoices(next);
    persist(next);
    setDecided(true);
    setShowPrefs(false);
    setShowBanner(false);
  };

  const declineAll = () => {
    const next = { necessary: true, functional: false, analytics: false, marketing: false };
    setChoices(next);
    persist(next);
    setDecided(true);
    setShowPrefs(false);
    setShowBanner(false);
  };

  const savePrefs = (newChoices) => {
    const next = { ...newChoices, necessary: true };
    setChoices(next);
    persist(next);
    setDecided(true);
    setShowPrefs(false);
    setShowBanner(false);
  };

  const allowed = (key) => Boolean(choices[key]);

  // Expose reopen helper
  useEffect(() => {
    window.openCookieSettings = () => setShowPrefs(true);
  }, []);

  const value = useMemo(
    () => ({
      choices,
      decided,
      setChoices: (patch) => setChoices((prev) => ({ ...prev, ...patch, necessary: true })),
      acceptAll,
      declineAll,
      savePrefs,
      allowed,
      open: () => setShowPrefs(true),
      close: () => setShowPrefs(false),
    }),
    [choices, decided]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Overlay blocks clicks until a decision */}
      {!decided && showBanner && <div style={overlayStyle} />}

      {!decided && showBanner && (
        <Banner
          onAccept={acceptAll}
          onDecline={declineAll}
          onManage={() => setShowPrefs(true)}
        />
      )}

      {showPrefs && (
        <Preferences
          choices={choices}
          onClose={() => setShowPrefs(false)}
          onSave={savePrefs}
        />
      )}
    </Ctx.Provider>
  );
}

export function CookieSettingsTrigger({ children, style = {}, className = "" }) {
  const { open } = useCookieConsent();
  const { t } = useLanguage();
  return (
    <button
      onClick={open}
      className={className}
      aria-label={t("cookie.settings")}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",   
        color: "inherit", 
        textDecoration: "underline",
        ...style,
      }}
    >
      {children || t("cookie.settings")}
    </button>
  );
}



/* ---- Banner (Accept / Decline / Manage) ---- */
function Banner({ onAccept, onDecline, onManage }) {
  const { t, withLanguage } = useLanguage();

  return (
    <div style={bannerStyle} role="dialog" aria-live="polite" aria-label={t("cookie.bannerAria")}>
      <p style={{ margin: 0 }}>
        {t("cookie.bannerTextBefore")}{" "}
        <a href={withLanguage("/privacy-policy")} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {t("common.privacyPolicy")}
        </a>
        {t("cookie.bannerTextAfter")}
      </p>
      <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
        <button onClick={onDecline} style={btnGhost}>{t("cookie.decline")}</button>
        <button onClick={onManage} style={btnOutline}>{t("cookie.manage")}</button>
        <button onClick={onAccept} style={btnPrimary}>{t("cookie.acceptAll")}</button>
      </div>
    </div>
  );
}

/* ---- Preferences Modal ---- */
function Preferences({ choices, onClose, onSave }) {
  const [localChoices, setLocalChoices] = useState(choices); // saját lokális state
  const { t } = useLanguage();

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSave = () => {
    onSave(localChoices); // itt mentjük csak vissza a fő state-be
  };

  return (
    <div role="dialog" aria-modal="true" aria-label={t("cookie.preferencesAria")} style={scrim}>
      <div style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>{t("cookie.preferencesTitle")}</h2>
          <button onClick={onClose} aria-label={t("cookie.close")} style={iconBtn}
           onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--color-danger)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "var(--color-control-bg)";
          }}
          >X</button>
        </div>
        <p style={{ color: "var(--color-muted)", marginTop: 8,  textAlign:'left'}}>
          {t("cookie.preferencesDescription")}
        </p>

        <Category
          title={t("cookie.categories.necessary.title")}
          description={t("cookie.categories.necessary.description")}
          checked
          disabled
        />
        <Category
          title={t("cookie.categories.functional.title")}
          description={t("cookie.categories.functional.description")}
          checked={localChoices.functional}
          onChange={(v) => setLocalChoices({ ...localChoices, functional: v })}
        />
        <Category
          title={t("cookie.categories.analytics.title")}
          description={t("cookie.categories.analytics.description")}
          checked={localChoices.analytics}
          onChange={(v) => setLocalChoices({ ...localChoices, analytics: v })}
        />
        <Category
          title={t("cookie.categories.marketing.title")}
          description={t("cookie.categories.marketing.description")}
          checked={localChoices.marketing}
          onChange={(v) => setLocalChoices({ ...localChoices, marketing: v })}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost}>{t("cookie.cancel")}</button>
          <button onClick={handleSave} style={btnPrimary}>{t("cookie.save")}</button>
        </div>
      </div>
    </div>
  );
}

function Category({ title, description, checked = false, disabled = false, onChange = () => {} }) {
  return (
    <label style={row}>
      <div>
        <div style={{ fontWeight: 600, textAlign:"left" }}>{title}</div>
        <div style={{ color: "var(--color-muted)", fontSize: 14 }}>{description}</div>
      </div>
      <input 
        type="checkbox" 
        checked={checked} 
        disabled={disabled} 
        onChange={(e) => onChange(e.target.checked)} 
      />
    </label>
  );
}

/* ---- Prior-consent helpers ---- */
const loaded = new Set();
export function loadScriptOnce(id, src, async = true) {
  if (loaded.has(id) || document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.src = src;
  s.async = async;
  document.head.appendChild(s);
  loaded.add(id);
}
export function removeScript(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
  loaded.delete(id);
}

/* ---- Styles ---- */
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  zIndex: 9998,
  pointerEvents: "auto", // blocks clicks
};

const bannerStyle = {
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100%",
  backgroundColor: "var(--color-cookie-bg)",
  color: "var(--color-cookie-text)",
  padding: "1rem",
  textAlign: "center",
  fontSize: "0.9rem",
  zIndex: 9999,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxShadow: "0 -2px 6px rgba(0, 0, 0, 0.3)",
};




const linkStyle = { color: "var(--color-cookie-link)", textDecoration: "underline" };
const btnBase = { border: "none", padding: "0.5rem 1rem", fontWeight: "bold", cursor: "pointer", borderRadius: 4, transition: "background-color .2s" };
const btnPrimary = { ...btnBase, backgroundColor: "var(--color-cookie-primary-bg)", color: "var(--color-cookie-primary-text)" };
const btnOutline = { ...btnBase, backgroundColor: "var(--color-cookie-outline-bg)", color: "#fff", border: "1px solid #ffffff55" };
const btnGhost = { ...btnBase, backgroundColor: "var(--color-danger)", color: "#fff" };

const scrim = { position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 10000, display: "grid", placeItems: "center", padding: 16 };
const modal = { width: "100%", maxWidth: 640, background: "var(--color-surface)", color: "var(--color-text)", borderRadius: 12, padding: 16, boxShadow: "var(--shadow-modal)", border: "1px solid var(--color-border)" };
const row = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid var(--color-border)", gap: 16 };
const iconBtn = { width: 36, height: 36, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-control-bg)", color: "var(--color-text)", cursor: "pointer", fontSize: 20, lineHeight: 1 };
